import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { toast } from 'sonner';

import {
  createPeer,
  generateSessionCode,
  normalizeSessionCode,
  codeToPeerId,
} from '@/services/supportSession';
import { getSessionCredentials } from '@/lib/credentialsStore';

/**
 * SupportSessionContext — hoists the entire Connect-for-Support session
 * (peer, data channel, media streams, chat log) OUT of the page
 * component so it survives cross-module navigation. The user can leave
 * the support page to demo an issue on any other screen and the
 * connection stays live; a floating pill offers a one-click return.
 *
 * The provider is mounted once at the app root — it holds refs to the
 * peer / call / streams and exposes state + actions via
 * `useSupportSession()`.
 */

const Ctx = createContext(null);
export const useSupportSession = () => useContext(Ctx);

// Strokes fade out after this long so the canvas doesn't get littered
// during a long session. Set high enough that a helper can point at
// something and the requester has plenty of time to see it.
export const ANNOTATION_TTL_MS = 6000;

// File transfer knobs. 16 KB is well below the SCTP MRU and copes
// nicely with the PeerJS ArrayBuffer wrapper. Bumping this makes
// transfers faster but risks the browser buffering too aggressively.
const FILE_CHUNK_BYTES = 16 * 1024;
const FILE_MAX_BYTES   = 50 * 1024 * 1024; // 50 MB
// Send-side throttle: pause when buffered amount exceeds this so we
// don't blow the peer connection's send queue on big files.
const FILE_BUFFER_HIGH_WATER = 512 * 1024;

export function formatBytes(n) {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function canShareScreen() {
  return !!(navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function');
}
export function isMobileBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
}

/**
 * Create a tiny disabled video track. WebRTC negotiates media
 * transceivers from the FIRST stream added to the peer connection.
 * If the helper calls with a mic-only stream, the SDP offer omits the
 * `m=video` line entirely, and the requester's answer can't attach
 * their screen-share video to a non-existent transceiver — you get
 * voice + chat but a black stage on the helper side.
 *
 * Adding a 2x2 canvas capture (disabled, so no frames are actually
 * encoded) forces the offer to include a video slot so the requester's
 * real screen video flows through it.
 */
function createDummyVideoTrack() {
  const canvas = document.createElement('canvas');
  canvas.width = 2;
  canvas.height = 2;
  const ctx = canvas.getContext('2d');
  if (ctx) { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, 2, 2); }
  // captureStream at 1 fps keeps the placeholder as light as possible.
  const stream = canvas.captureStream ? canvas.captureStream(1) : null;
  const track = stream?.getVideoTracks?.()[0];
  if (track) track.enabled = false; // send no real frames
  return track || null;
}

function prettyPeerErr(e) {
  if (!e) return 'Something went wrong.';
  const t = e.type || e.name || '';
  if (t === 'unavailable-id')       return 'That code is already in use. Please generate a new one.';
  if (t === 'peer-unavailable')     return "We couldn't find that session — check the code or ask them to start a fresh one.";
  if (t === 'network' || t === 'server-error') return 'Network issue reaching the signaling server. Try again in a moment.';
  if (t === 'browser-incompatible') return 'This browser cannot host peer-to-peer sessions. Please try Chrome / Edge / Firefox.';
  return e.message || 'Unable to establish the session.';
}

export function SupportSessionProvider({ children }) {
  const [mode, setMode]           = useState('idle'); // 'idle' | 'request' | 'help' | 'session'
  const [role, setRole]           = useState(null);   // 'requester' | 'helper'
  const [code, setCode]           = useState('');
  const [inputCode, setInputCode] = useState('');
  const [status, setStatus]       = useState('');
  const [error, setError]         = useState('');
  const [connecting, setConnecting] = useState(false);

  const [localStream, setLocalStream]   = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [micOn, setMicOn]           = useState(true);
  const [screenOn, setScreenOn]     = useState(false);
  const [messages, setMessages]     = useState([]);

  // Identity exchange — populated once the data channel is open and
  // the other side has sent their `hello`. Requester's identity comes
  // from the stored session credentials (licence + user). Helper's
  // identity comes from a free-text name they enter before connecting.
  const [helperName, setHelperName] = useState('');
  const [remoteIdentity, setRemoteIdentity] = useState(null); // { role, licenceid?, username?, displayName? }

  // Annotations — freehand strokes drawn by either peer over the
  // shared screen. Coordinates are normalised (0..1) so both sides
  // can render at any size. Strokes fade after ANNOTATION_TTL_MS.
  const [strokes, setStrokes] = useState({}); // { [id]: { id, points: [{x,y}], color, width, at, remote } }

  // File transfers — one entry per file, in either direction.
  const [transfers, setTransfers] = useState({}); // { [id]: { id, name, size, mime, dir, status, sent, received, blobUrl, sender } }
  const incomingChunksRef = useRef({}); // { [id]: Uint8Array[] }

  const peerRef        = useRef(null);
  const dataConnRef    = useRef(null);
  const mediaCallRef   = useRef(null);
  const micStreamRef   = useRef(null);
  const displayStreamRef = useRef(null);

  const pushMsg = useCallback((m) => {
    setMessages((prev) => [...prev, { ...m, at: Date.now() }]);
  }, []);

  const teardown = useCallback(() => {
    try { mediaCallRef.current?.close(); } catch { /* ignore */ }
    try { dataConnRef.current?.close(); } catch { /* ignore */ }
    try { peerRef.current?.destroy(); } catch { /* ignore */ }
    [micStreamRef.current, displayStreamRef.current].forEach((s) => {
      s?.getTracks?.().forEach((t) => t.stop());
    });
    peerRef.current = null;
    dataConnRef.current = null;
    mediaCallRef.current = null;
    micStreamRef.current = null;
    displayStreamRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setMicOn(true);
    setScreenOn(false);
  }, []);

  const endSession = useCallback(() => {
    teardown();
    // Revoke any blob URLs from received files so we don't leak.
    setTransfers((prev) => {
      Object.values(prev).forEach((t) => { if (t.blobUrl) URL.revokeObjectURL(t.blobUrl); });
      return {};
    });
    incomingChunksRef.current = {};
    setStrokes({});
    setMessages([]);
    setCode('');
    setInputCode('');
    setStatus('');
    setError('');
    setMode('idle');
    setRole(null);
    setRemoteIdentity(null);
  }, [teardown]);

  /* ------------------- media helpers ------------------- */

  const getRequesterStream = useCallback(async () => {
    if (!canShareScreen()) throw new Error('Screen sharing is not supported in this browser.');
    const display = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 15 }, audio: false });
    displayStreamRef.current = display;

    let mic = null;
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      micStreamRef.current = mic;
    } catch { /* mic optional */ }

    const combined = new MediaStream([
      ...display.getVideoTracks(),
      ...(mic ? mic.getAudioTracks() : []),
    ]);

    setLocalStream(combined);
    setScreenOn(true);
    setMicOn(!!mic);

    display.getVideoTracks()[0]?.addEventListener('ended', () => {
      setScreenOn(false);
      toast.info('Screen sharing stopped. Click "Share my screen" to resume.');
    });
    return combined;
  }, []);

  const startScreenShare = useCallback(async () => {
    setError('');
    try {
      const stream = await getRequesterStream();
      const call = mediaCallRef.current;
      if (call?.peerConnection) {
        const pc = call.peerConnection;
        const senders = pc.getSenders();
        stream.getTracks().forEach((track) => {
          const sender = senders.find((s) => s.track && s.track.kind === track.kind);
          if (sender) sender.replaceTrack(track);
          else pc.addTrack(track, stream);
        });
      }
      toast.success('Screen is now being shared.');
    } catch (e) {
      const cancelled = e?.name === 'NotAllowedError' || /permission/i.test(e?.message || '');
      setError(cancelled
        ? 'You cancelled the screen-share prompt. Click "Share my screen" whenever you\'re ready.'
        : (e?.message || 'Could not share your screen.'));
    }
  }, [getRequesterStream]);

  /* ------------------- annotations ------------------- */

  const upsertStroke = useCallback((op, payload) => {
    setStrokes((prev) => {
      const next = { ...prev };
      if (op === 'begin') {
        next[payload.id] = {
          id: payload.id,
          kind: payload.kind || 'pen',
          color: payload.color || '#ef4444',
          width: payload.width || 3,
          points: [{ x: payload.x, y: payload.y }],
          text: '',
          at: Date.now(),
          remote: !!payload.remote,
        };
      } else if (op === 'point' && next[payload.id]) {
        const s = next[payload.id];
        // For shapes (arrow/rect) we only ever keep TWO points — start
        // stays fixed at [0], the current drag position lives at [1].
        // Freehand pen keeps appending.
        const isShape = s.kind === 'arrow' || s.kind === 'rect';
        const points = isShape
          ? [s.points[0], { x: payload.x, y: payload.y }]
          : [...s.points, { x: payload.x, y: payload.y }];
        next[payload.id] = { ...s, points, at: Date.now() };
      } else if (op === 'text') {
        next[payload.id] = {
          id: payload.id,
          kind: 'text',
          color: payload.color || '#ef4444',
          width: payload.width || 3,
          points: [{ x: payload.x, y: payload.y }],
          text: payload.text || '',
          at: Date.now(),
          remote: !!payload.remote,
        };
      }
      return next;
    });
  }, []);

  const handleRemoteDraw = useCallback((data) => {
    if (data.op === 'clear') {
      setStrokes({});
      return;
    }
    if (data.op === 'begin' || data.op === 'point' || data.op === 'text') {
      upsertStroke(data.op, { ...data, remote: true });
    }
    // 'end' currently has no side effect — auto-fade handles cleanup.
  }, [upsertStroke]);

  const sendDrawEvent = useCallback((event) => {
    // Local render first for snappy feedback.
    if (event.op === 'begin' || event.op === 'point' || event.op === 'text') {
      upsertStroke(event.op, { ...event, remote: false });
    } else if (event.op === 'clear') {
      setStrokes({});
    }
    if (dataConnRef.current?.open) {
      try { dataConnRef.current.send({ type: 'draw', ...event }); } catch { /* ignore */ }
    }
  }, [upsertStroke]);

  const clearAnnotations = useCallback(() => sendDrawEvent({ op: 'clear' }), [sendDrawEvent]);

  // Auto-fade: sweep expired strokes every second.
  useEffect(() => {
    const t = setInterval(() => {
      setStrokes((prev) => {
        const now = Date.now();
        let changed = false;
        const next = {};
        for (const [id, s] of Object.entries(prev)) {
          if (now - s.at < ANNOTATION_TTL_MS) next[id] = s;
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  /* ------------------- file transfer ------------------- */

  const handleFileOffer = useCallback((data) => {
    incomingChunksRef.current[data.id] = [];
    setTransfers((prev) => ({
      ...prev,
      [data.id]: {
        id: data.id, name: data.name, size: data.size, mime: data.mime || '',
        dir: 'incoming', status: 'receiving', received: 0, sender: data.sender || '',
      },
    }));
    pushMsg({ from: 'system', text: `Incoming file: ${data.name} (${formatBytes(data.size)})` });
  }, [pushMsg]);

  const handleFileChunk = useCallback((data) => {
    // `data.data` is either a Uint8Array (BinaryPack) or an ArrayBuffer.
    const buf = data.data instanceof Uint8Array
      ? data.data
      : new Uint8Array(data.data);
    const list = incomingChunksRef.current[data.id];
    if (!list) return;
    list.push(buf);
    setTransfers((prev) => {
      const t = prev[data.id];
      if (!t) return prev;
      return { ...prev, [data.id]: { ...t, received: t.received + buf.byteLength } };
    });
  }, []);

  const handleFileEnd = useCallback((data) => {
    const chunks = incomingChunksRef.current[data.id];
    delete incomingChunksRef.current[data.id];
    setTransfers((prev) => {
      const t = prev[data.id];
      if (!t) return prev;
      if (!chunks) return { ...prev, [data.id]: { ...t, status: 'failed' } };
      const blob = new Blob(chunks, { type: t.mime || 'application/octet-stream' });
      const blobUrl = URL.createObjectURL(blob);
      return { ...prev, [data.id]: { ...t, status: 'done', blobUrl, received: t.size } };
    });
    pushMsg({ from: 'system', text: `File received: ${data.name || 'file'}` });
  }, [pushMsg]);

  const sendFile = useCallback(async (file) => {
    if (!file) return;
    if (!dataConnRef.current?.open) { toast.error('Not connected yet.'); return; }
    if (file.size > FILE_MAX_BYTES) {
      toast.error(`File is too large (max ${formatBytes(FILE_MAX_BYTES)}).`);
      return;
    }
    const id = `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const dc = dataConnRef.current;
    dc.send({
      type: 'file-offer',
      id, name: file.name, size: file.size, mime: file.type || 'application/octet-stream',
      sender: role || '',
    });

    setTransfers((prev) => ({
      ...prev,
      [id]: {
        id, name: file.name, size: file.size, mime: file.type,
        dir: 'outgoing', status: 'sending', sent: 0,
      },
    }));

    // Wait until the underlying RTCDataChannel drains below the high
    // water mark. `bufferedAmount` on PeerJS is exposed via its
    // internal `_dc`, so we read it defensively.
    const waitForDrain = () => new Promise((resolve) => {
      const raw = dc.dataChannel;
      if (!raw || raw.bufferedAmount < FILE_BUFFER_HIGH_WATER) return resolve();
      raw.addEventListener('bufferedamountlow', () => resolve(), { once: true });
      raw.bufferedAmountLowThreshold = FILE_BUFFER_HIGH_WATER / 2;
    });

    try {
      const reader = file.stream().getReader();
      let sent = 0;
      let leftover = new Uint8Array(0);
      // Read stream, re-chunk into FILE_CHUNK_BYTES, send.
      while (true) {
        const { done, value } = await reader.read();
        if (done && leftover.byteLength === 0) break;
        let src;
        if (done) { src = leftover; leftover = new Uint8Array(0); }
        else {
          const merged = new Uint8Array(leftover.byteLength + value.byteLength);
          merged.set(leftover, 0); merged.set(value, leftover.byteLength);
          src = merged;
        }
        let offset = 0;
        while (src.byteLength - offset >= FILE_CHUNK_BYTES || (done && src.byteLength - offset > 0)) {
          const end = Math.min(offset + FILE_CHUNK_BYTES, src.byteLength);
          const chunk = src.slice(offset, end);
          await waitForDrain();
          dc.send({ type: 'file-chunk', id, data: chunk });
          sent += chunk.byteLength;
          setTransfers((prev) => {
            const t = prev[id]; if (!t) return prev;
            return { ...prev, [id]: { ...t, sent } };
          });
          offset = end;
        }
        leftover = src.slice(offset);
        if (done) break;
      }
      dc.send({ type: 'file-end', id, name: file.name });
      setTransfers((prev) => {
        const t = prev[id]; if (!t) return prev;
        return { ...prev, [id]: { ...t, status: 'done', sent: file.size } };
      });
      pushMsg({ from: 'system', text: `File sent: ${file.name}` });
    } catch (e) {
      setTransfers((prev) => {
        const t = prev[id]; if (!t) return prev;
        return { ...prev, [id]: { ...t, status: 'failed' } };
      });
      toast.error(`File send failed: ${e?.message || 'unknown error'}`);
    }
  }, [pushMsg, role]);

  const removeTransfer = useCallback((id) => {
    setTransfers((prev) => {
      const t = prev[id];
      if (t?.blobUrl) URL.revokeObjectURL(t.blobUrl);
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  /* ------------------- data channel bindings ------------------- */

  /**
   * Build the identity payload the local side should send on data
   * channel open. Requester's identity comes from the stored session
   * credentials (populated at login); helper's identity is the
   * free-text name they optionally typed on the landing screen.
   */
  const buildLocalHello = useCallback(async (localRole) => {
    if (localRole === 'requester') {
      const creds = await getSessionCredentials().catch(() => null);
      return {
        type: 'hello',
        role: 'requester',
        licenceid: creds?.licenceid || '',
        username: creds?.username || '',
      };
    }
    return {
      type: 'hello',
      role: 'helper',
      displayName: (helperName || '').trim(),
    };
  }, [helperName]);

  const attachDataConn = useCallback((dc, localRole) => {
    dataConnRef.current = dc;
    dc.on('open', async () => {
      setStatus('');
      setConnecting(false);
      setMode('session');
      pushMsg({ from: 'system', text: 'Session connected. Everything is peer-to-peer — nothing goes through a server.' });
      try {
        const hello = await buildLocalHello(localRole);
        dc.send(hello);
      } catch { /* identity is best-effort */ }
    });
    dc.on('data', (data) => {
      if (!data || typeof data !== 'object') return;
      if (data.type === 'chat' && typeof data.text === 'string') {
        pushMsg({ from: 'remote', text: data.text });
      } else if (data.type === 'hello') {
        setRemoteIdentity({
          role: data.role,
          licenceid: data.licenceid || '',
          username: data.username || '',
          displayName: data.displayName || '',
        });
      } else if (data.type === 'draw') {
        handleRemoteDraw(data);
      } else if (data.type === 'file-offer') {
        handleFileOffer(data);
      } else if (data.type === 'file-chunk') {
        handleFileChunk(data);
      } else if (data.type === 'file-end') {
        handleFileEnd(data);
      }
    });
    dc.on('close', () => {
      pushMsg({ from: 'system', text: 'The other side ended the session.' });
      setTimeout(endSession, 1200);
    });
    dc.on('error', () => setError('Chat channel lost.'));
  }, [endSession, pushMsg, buildLocalHello, handleRemoteDraw, handleFileOffer, handleFileChunk, handleFileEnd]);

  const bindCall = useCallback((call) => {
    mediaCallRef.current = call;
    call.on('stream', (stream) => setRemoteStream(stream));
    call.on('close', () => setRemoteStream(null));
    call.on('error', () => setError('Media stream error.'));
  }, []);

  /* ------------------- flow: requester ------------------- */

  const startAsRequester = useCallback(async () => {
    setError('');
    if (!canShareScreen()) {
      setError(
        isMobileBrowser()
          ? "This device can't share its screen — mobile browsers don't support screen capture. Ask the helper to share their screen with you instead, or switch to a laptop / desktop."
          : "Your browser doesn't support screen sharing. Please use the latest Chrome, Edge, Firefox, or Safari on a laptop / desktop.",
      );
      return;
    }
    setMode('request');
    setRole('requester');
    setConnecting(true);
    try {
      let session = null;
      let lastErr = null;
      for (let i = 0; i < 3 && !session; i++) {
        const c = generateSessionCode();
        try {
          const { peer, id } = await createPeer(codeToPeerId(c));
          peerRef.current = peer;
          session = { code: c, id };
        } catch (e) { lastErr = e; }
      }
      if (!session) throw lastErr || new Error('Could not open a support session');

      setCode(session.code);
      setStatus('Waiting for a helper to join…');
      setConnecting(false);

      peerRef.current.on('connection', (dc) => attachDataConn(dc, 'requester'));
      peerRef.current.on('call', async (call) => {
        let stream = null;
        try {
          stream = await getRequesterStream();
        } catch (e) {
          const cancelled = e?.name === 'NotAllowedError' || /permission/i.test(e?.message || '');
          setError(cancelled
            ? 'You cancelled the screen-share prompt. Use "Share my screen" in the session to try again.'
            : (e?.message || 'Could not share your screen. Use "Share my screen" to retry.'));
          try {
            const mic = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            micStreamRef.current = mic;
            stream = mic;
            setLocalStream(mic);
            setMicOn(true);
          } catch { /* no mic either */ }
        }
        // Ensure the answer always carries a video track. If screen
        // share failed / was cancelled, use a dummy so the WebRTC
        // video sender is initialised — that lets `replaceTrack()`
        // later swap in the real screen once the user retries.
        const answerTracks = stream ? [...stream.getTracks()] : [];
        if (!answerTracks.some((t) => t.kind === 'video')) {
          const dummy = createDummyVideoTrack();
          if (dummy) answerTracks.push(dummy);
        }
        const answerStream = new MediaStream(answerTracks);
        call.answer(answerStream);
        bindCall(call);
        setMode('session');
        setStatus('');
      });
      peerRef.current.on('error', (e) => setError(prettyPeerErr(e)));
    } catch (e) {
      setConnecting(false);
      setError(prettyPeerErr(e));
    }
  }, [attachDataConn, bindCall, getRequesterStream]);

  /* ------------------- flow: helper ------------------- */

  const startAsHelper = useCallback(() => {
    setError('');
    setMode('help');
    setRole('helper');
  }, []);

  const connectToRequester = useCallback(async () => {
    const target = normalizeSessionCode(inputCode);
    if (!target || target.length < 7) {
      setError('Enter the full session code (like CHK-4A82).');
      return;
    }
    setError('');
    setConnecting(true);
    setStatus('Connecting…');
    try {
      const { peer } = await createPeer(undefined);
      peerRef.current = peer;

      const remoteId = codeToPeerId(target);

      let micStream = null;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        micStreamRef.current = micStream;
        setLocalStream(micStream);
      } catch {
        toast.info('Continuing without microphone — you can still see the screen and chat.');
      }

      const dc = peer.connect(remoteId, { reliable: true });
      attachDataConn(dc, 'helper');

      // Build the outgoing stream: mic audio + a dummy video track so
      // the WebRTC offer includes an m=video line. Without this, the
      // requester's screen video is silently dropped (voice works but
      // stage is black — the exact bug we observed).
      const outTracks = [];
      if (micStream) outTracks.push(...micStream.getAudioTracks());
      const dummyVideo = createDummyVideoTrack();
      if (dummyVideo) outTracks.push(dummyVideo);
      const outStream = new MediaStream(outTracks);

      const call = peer.call(remoteId, outStream);
      bindCall(call);

      peer.on('error', (e) => setError(prettyPeerErr(e)));

      setTimeout(() => {
        if (!dataConnRef.current?.open) {
          setError('Could not reach that session. Double-check the code and try again.');
          setConnecting(false);
        }
      }, 8000);
    } catch (e) {
      setConnecting(false);
      setError(prettyPeerErr(e));
    }
  }, [inputCode, attachDataConn, bindCall]);

  /* ------------------- in-session actions ------------------- */

  const toggleMic = useCallback(() => {
    const stream = micStreamRef.current;
    if (!stream) return;
    const enabled = !micOn;
    stream.getAudioTracks().forEach((t) => { t.enabled = enabled; });
    setMicOn(enabled);
  }, [micOn]);

  const sendChat = useCallback((text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || !dataConnRef.current?.open) return;
    dataConnRef.current.send({ type: 'chat', text: trimmed });
    pushMsg({ from: 'me', text: trimmed });
  }, [pushMsg]);

  const downloadTranscript = useCallback(() => {
    if (messages.length === 0) { toast.info('No chat to save yet.'); return; }
    const lines = messages.map((m) => {
      const t = new Date(m.at).toLocaleTimeString();
      const who = m.from === 'me' ? 'You' : m.from === 'remote' ? (role === 'helper' ? 'Requester' : 'Helper') : 'System';
      return `[${t}] ${who}: ${m.text}`;
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `chanakya-support-${code || 'session'}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }, [messages, code, role]);

  const copyCode = useCallback(async () => {
    try { await navigator.clipboard.writeText(code); toast.success('Code copied'); }
    catch { toast.error('Copy failed — please read the code'); }
  }, [code]);

  const isActive = mode === 'session' || mode === 'request';

  const value = useMemo(() => ({
    // state
    mode, role, code, inputCode, status, error, connecting,
    localStream, remoteStream, micOn, screenOn, messages, isActive,
    helperName, remoteIdentity,
    strokes, transfers,
    // setters
    setInputCode, setHelperName,
    // actions
    startAsRequester, startAsHelper, connectToRequester, endSession,
    toggleMic, sendChat, downloadTranscript, copyCode, startScreenShare,
    sendDrawEvent, clearAnnotations, sendFile, removeTransfer,
  }), [
    mode, role, code, inputCode, status, error, connecting,
    localStream, remoteStream, micOn, screenOn, messages, isActive,
    helperName, remoteIdentity,
    strokes, transfers,
    startAsRequester, startAsHelper, connectToRequester, endSession,
    toggleMic, sendChat, downloadTranscript, copyCode, startScreenShare,
    sendDrawEvent, clearAnnotations, sendFile, removeTransfer,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
